import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

import LoginPage from "./pages/LoginPage";
import HomeScreen from "./pages/HomeScreen";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { decodeJwt } from "./util/decodeJwt";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import { useOrganizationApi } from "./api/organization";

/* =====================================================
   HELPERS
===================================================== */
export function getSubdomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  // e.g. "jsagro.bcuai.in" → ["jsagro", "bcuai", "in"] → "jsagro"
  // localhost → no subdomain
  if (parts.length >= 3) return parts[0];
  return null;
}

export function isAdminDomain() {
  const hostname = window.location.hostname;
  // localhost / 127.0.0.1 always → admin theme
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  return getSubdomain() === "akira";
}

export function applyPrimaryColor(color) {
  if (color) {
    document.body.style.setProperty("--primary-color", color);
  } else {
    document.body.style.removeProperty("--primary-color");
  }
}

/* =====================================================
   ROUTER WRAPPER
===================================================== */
export default function AppWrapper() {
  return (
    <BrowserRouter>
      <AppRoutes />
      <ToastContainer position="top-right" autoClose={3000} />
    </BrowserRouter>
  );
}

/* =====================================================
   MAIN ROUTES
===================================================== */
function AppRoutes() {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("token")
  );

  const navigate = useNavigate();
  const { getOrgBySubdomain, getVendorOrgById } = useOrganizationApi();

  /* =====================================================
     FETCH ORG PRIMARY COLOR BY SUBDOMAIN
  ===================================================== */
  async function fetchAndApplyOrgColor(orgId = null) {
    const subdomain = getSubdomain();

    try {
      let org;
      if (orgId) {
        // Logged in: fetch by Org ID (works on localhost/production)
        org = await getVendorOrgById(orgId, { showLoader: false });
      } else {
        // Not logged in: fetch by subdomain
        const effectiveSubdomain = isAdminDomain() ? (subdomain || "akira") : subdomain;
        if (!effectiveSubdomain) return;
        org = await getOrgBySubdomain(effectiveSubdomain, { showLoader: false });
      }

      if (!org) return;

      // Store primary color
      const color = org?.primary_color;
      if (color) {
        localStorage.setItem("primaryColor", color);
        applyPrimaryColor(color);
      }

      // Store org name
      const orgName = org?.name || "";
      if (orgName) {
        localStorage.setItem("orgName", orgName);
      }

      // Store org logo
      if (org?.logo) {
        localStorage.setItem("orgLogo", org.logo);
      } else {
        localStorage.removeItem("orgLogo");
      }

      // Notify all components that branding data is ready
      window.dispatchEvent(new Event("branding-updated"));
    } catch (err) {
      console.warn("Could not fetch branding:", err.message);
    }
  }

  /* =====================================================
     APPLY THEME ON MOUNT (page load) + LOGIN STATE CHANGE
     - On every load: set theme class immediately from cache
     - Then fetch fresh color from server in background
  ===================================================== */
  useEffect(() => {
    document.body.classList.remove("admin-theme", "manufacturer-theme");

    const token = localStorage.getItem("token");
    let loggedInOrgId = null;
    let loggedInRole = null;

    if (token) {
      try {
        const decoded = decodeJwt(token);
        loggedInOrgId = decoded?.organizationId;
        loggedInRole = decoded?.role?.toLowerCase();
      } catch (e) {}
    }

    // theme selection logic
    if (loggedInRole === "admin") {
      document.body.classList.add("admin-theme");
    } else if (loggedInRole === "manufacturer" || loggedInRole === "distributor" || loggedInRole === "dealer") {
      document.body.classList.add("manufacturer-theme");
    } else {
      // Not logged in: fallback to domain logic
      if (isAdminDomain()) {
        document.body.classList.add("admin-theme");
      } else {
        document.body.classList.add("manufacturer-theme");
      }
    }

    // Apply cached color immediately to prevent flash
    const cachedColor = localStorage.getItem("primaryColor");
    if (cachedColor) applyPrimaryColor(cachedColor);

    // Fetch fresh branding (uses token org if available, else subdomain)
    fetchAndApplyOrgColor(loggedInOrgId);
  }, [isLoggedIn]); // runs on mount and whenever login state changes

  /* =====================================================
     LOGOUT
  ===================================================== */
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    // Keep orgLogo, orgName, primaryColor — they're branding cache, not auth data.
    // LoginPage seeds from them so the logo still appears on the login screen.

    setIsLoggedIn(false);

    document.body.classList.remove("admin-theme", "manufacturer-theme");

    if (isAdminDomain()) {
      document.body.classList.add("admin-theme");
    } else {
      document.body.classList.add("manufacturer-theme");
      // color will be re-fetched by useEffect after isLoggedIn changes
    }

    toast.success("Logout successful!");
    navigate("/", { replace: true });
  };

  /* =====================================================
     LOGIN SUCCESS
  ===================================================== */
  const handleLoginSuccess = async () => {
    setIsLoggedIn(true);

    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      const decoded = decodeJwt(token);
      const role = decoded?.role || "manufacturer";

      localStorage.setItem("userRole", role);

      if (role === "admin") {
        navigate("/inventory-management", { replace: true });
      } else if (role === "manufacturer") {
        navigate("/maintenance-overview", { replace: true });
      } else {
        navigate("/inventory-management", { replace: true });
      }
    } catch (err) {
      console.error("JWT decode failed", err);
      navigate("/", { replace: true });
    }
  };

  /* =====================================================
     ROUTES
  ===================================================== */
  return (
    <Routes>
      {isLoggedIn ? (
        <>
          <Route
            path="/*"
            element={<HomeScreen onLogout={handleLogout} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <>
          <Route
            path="/"
            element={<LoginPage onLoginSuccess={handleLoginSuccess} />}
          />
          <Route
            path="/forgot-password"
            element={<ForgotPasswordPage />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
}
